/* eslint-disable no-return-assign */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import moment from 'moment';
import LoadingPageText from '../../../common/Loading/LoadingPageText';
import globals from '../../../../globals/communityGlobals';
import useTimeline from '../../timeline/useTimeline';
import _ from '../../../../utils/lodash';

import { useFetchCancellationReasonsOptions } from '../../../../actions/areas/planner/fetchHooks';
import { bulkCancelTreatmentInstances } from '../../../../actions/entities';
import { setBulkCancelProgress } from '../../../../actions/areas/dailyPlanning/moreOptions';

export default function BulkCancel({ items, toggleRefresh, includeTreatments }) {
  const [state, setState] = useState({
    selectedReason: null,
    riskAssesment: '',
    selectedCancellingNoticeDate: moment(Date.now()),
    saveErrors: null,
  });

  const isDataLoading = useSelector(store => store.areas.dailyPlanning.moreOptions.isBulkCancelInProgress);

  const riskMaxLength = 500;
  const locale = globals.getArea('treatments');
  const errorsLocale = globals.getArea('error');
  const selectedDate = useSelector(store => store.areas.dailyPlanning.selectedDate);

  const dispatch = useDispatch();
  const { refreshClinicianTreatmentsAndClosePanels, resetMultiSelectedTreatments } = useTimeline();

  const { data: cancellationReasonsOptions } = useFetchCancellationReasonsOptions(false);

  const isDisabledSave = () => {
    return (
      !state.selectedReason || !state.riskAssesment || state.riskAssesment.length > riskMaxLength || state.saveErrors
    );
  };

  const setCancellationFields = () => {
    items = items.map(obj => ({
      ...obj,
      riskAssesment: state.riskAssesment,
      reasonId: state.selectedReason,
      timeSlotId: obj.timeSlotId,
      dateAt: selectedDate,
    }));
  };

  const cancelTreatments = () => {
    setCancellationFields();
    dispatch(setBulkCancelProgress(true));
    dispatch(bulkCancelTreatmentInstances(items)).then(response => {
      const data = response.response.data;
      if (data.response.length !== 0) {
        setState({ ...state, saveErrors: data.response });
        refreshClinicianTreatmentsAndClosePanels();
      } else {
        refreshClinicianTreatmentsAndClosePanels();
        resetMultiSelectedTreatments();
      }
      dispatch(setBulkCancelProgress(false));
      if (includeTreatments) toggleRefresh();
    });
  };

  const mapPatients = () => {
    const groupedPatients = _.groupBy(items, 'patientName');

    const result = Object.keys(groupedPatients).map(key => {
      return { name: key, count: groupedPatients[key].length };
    });
    return result.map(patient => (
      <div>
        <Grid item className="npa-clinician-list-item">
          <span style={{ paddingRight: '5px' }}>{patient.name}</span>
          {patient.count > 1 ? <span>({patient.count})</span> : null}
        </Grid>
      </div>
    ));
  };

  const headerInfo = () => {
    return (
      <div className="sidePanel-header form-header">
        <div className="sidePanel-header-subtitle">
          <Typography variant="subtitle1">Bulk cancel visits</Typography>
        </div>
      </div>
    );
  };

  const mainContent = () => {
    return (
      <div className="sidePanel-content">
        <div className="clinician-info-box">
          <h6>Details</h6>
          <List className="list-underline">
            <ListItem key="npasp-clinicians-item">
              <ListItemText primary="Patients" />
              <span className="divider" />
              <Grid container className="npa-clinician-list">
                {mapPatients()}
              </Grid>
            </ListItem>
          </List>
        </div>
        <div className="cancel-sidePanel-form">
          <Typography variant="h6" gutterBottom>
            {`${locale.getMsg('cancellationReason')} *`}
          </Typography>
          <Select
            multiple={false}
            onChange={e => setState({ ...state, selectedReason: e.target.value })}
            disabled={isDataLoading}
          >
            {cancellationReasonsOptions &&
              cancellationReasonsOptions.map(cancelReason => (
                <MenuItem key={cancelReason.id} value={cancelReason.id}>
                  {cancelReason.name}
                </MenuItem>
              ))}
          </Select>
          <Typography variant="h6" gutterBottom>
            {`${locale.getMsg('riskAssessment')} *`}
          </Typography>
          <TextField
            required
            multiline
            disabled={isDataLoading}
            value={state.riskAssesment}
            error={state.riskAssesment && state.riskAssesment.length > riskMaxLength}
            helperText={errorsLocale.getMsg('validationMaxLength', [`${riskMaxLength}`])}
            onChange={e => setState({ ...state, riskAssesment: e.target.value })}
          />
        </div>
      </div>
    );
  };

  const errorMsgs = () => {
    return state.saveErrors.map(error => (
      <Grid className="form-validation-error">
        <p>{`${error.exceptionMessage} for patient ${error.patientName}`}</p>
      </Grid>
    ));
  };

  const actionButtons = () => {
    return (
      <div className="sidePanel-buttons">
        <div className="sidePanel-BackButton">
          <Button
            variant="contained"
            color="secondary"
            disabled={isDataLoading}
            onClick={() => resetMultiSelectedTreatments()}
          >
            Back
          </Button>
        </div>
        <div className="sidePanel-deactivateButton">
          <Button
            variant="contained"
            color="primary"
            disabled={isDisabledSave() || isDataLoading}
            onClick={cancelTreatments}
          >
            Save
          </Button>
        </div>
      </div>
    );
  };

  return (
    <section className="side-panel-content-wrapper">
      <SwipeableDrawer
        anchor="right"
        open={!isDataLoading}
        onOpen={() => {}}
        onClose={() => resetMultiSelectedTreatments()}
      >
        <div className="sidePanel-wrapper" style={{ width: '500px' }} id="bulk-cancel-side-panel">
          {headerInfo()}
          {isDataLoading ? <LoadingPageText showHeader={false} /> : null}
          {state.saveErrors && errorMsgs()}
          {mainContent()}
          {actionButtons()}
        </div>
      </SwipeableDrawer>
    </section>
  );
}
